import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { ReactComponent as LiteBgImage } from "../../assets/icons/svg/triva_lite.svg";
import { ReactComponent as SparkBgImage } from "../../assets/icons/svg/triva_spark.svg";
import { ReactComponent as IgniteBgImage } from "../../assets/icons/svg/triva_ignite.svg";
import { ReactComponent as ThriveBgImage } from "../../assets/icons/svg/triva_thrive.svg";
import { ReactComponent as LiteBgImageIcon } from "../../assets/icons/svg/triva_lite-icon.svg";
import { ReactComponent as SparkBgImageIcon } from "../../assets/icons/svg/triva_spark-icon.svg";
import { ReactComponent as IgniteBgImageIcon } from "../../assets/icons/svg/triva_ignite-icon.svg";
import { ReactComponent as ThriveBgImageIcon } from "../../assets/icons/svg/triva_thrive-icon.svg";
import GSTCalculator, { calculateGST } from "../ui/GSTCalculator";
import { API_URLS } from "../api/urls";

interface RecommendedPlanDetailsProps {
  planType: "lite" | "spark" | "ignite" | "thrive";
  userId: number | null;
  authToken: string | null;
  onBack: () => void;
  onSelectPlan: (planData: PlanData, selectedDuration: string) => void;
}

interface PlanData {
  title: string;
  description: string;
  price: string;
  originalPrice?: string;
  priceLabel: string;
  icon: React.ReactNode;
  bgColor: string;
  borderColor: string;
  borderHex: string;
  BgImageComponent: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  summary: string;
  perfectFor: string[];
  whatYouGet: string[];
  whatsNotIncluded: string[];
  programDetails?: string[];
  featuresTable?: {
    headers: string[];
    rows: {
      feature: string;
      values: string[];
    }[];
  };
  pricingOptions: {
    duration: string;
    price: string;
    originalPrice?: string;
    productId: string;
    standardDetailsId: number;
  }[];
}

interface ApiPlanData {
  subscription_plan_id: number;
  subscription_plan_name: string;
  std_subscription_perks: string;
  duration_and_price_and_perks: {
    standard_details_id: number;
    std_subscription_price: string;
    std_subscription_discount_price: string;
    duration_period: string;
    product_id: string;
    std_subscription_description: string;
    std_subscription_perfect_for: string;
    std_subscription_not_included: string;
    std_subscription_features: string;
  }[];
}

interface ApiResponse {
  success: string;
  message: string;
  plans: ApiPlanData[];
}

// Static visual configurations
const staticPlanConfigs = {
  lite: {
    icon: <LiteBgImageIcon className="w-[36px] h-[36px]" />,
    bgColor: "bg-[#FDEFFA]",
    borderColor: "border-[#E47DCB] border-[0.5px]",
    borderHex: "#E47DCB",
    BgImageComponent: LiteBgImage,
  },
  spark: {
    icon: <SparkBgImageIcon className="w-[36px] h-[36px]" />,
    bgColor: "bg-[#FDF6E4]",
    borderColor: "border-[#C5A344] border-[0.5px]",
    borderHex: "#C5A344",
    BgImageComponent: SparkBgImage,
  },
  ignite: {
    icon: <IgniteBgImageIcon className="w-[36px] h-[36px]" />,
    bgColor: "bg-[#EDF9D3]",
    borderColor: "border-[#86BC0B] border-[0.5px]",
    borderHex: "#86BC0B",
    BgImageComponent: IgniteBgImage,
  },
  thrive: {
    icon: <ThriveBgImageIcon className="w-[36px] h-[36px]" />,
    bgColor: "bg-[#EAE6FB]",
    borderColor: "border-[#B7AAF5] border-[0.5px]",
    borderHex: "#B7AAF5",
    BgImageComponent: ThriveBgImage,
  },
};

// Plan Selection Popup Component
interface PlanSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  planData: PlanData | null;
  onProceedToPayment: (selectedDuration: string) => void;
}

const PlanSelectionPopup: React.FC<PlanSelectionPopupProps> = ({
  isOpen,
  onClose,
  planData,
  onProceedToPayment,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimatingOut(false);
      setTimeout(() => {
        setIsAnimatingIn(true);
      }, 10);
    } else {
      setIsAnimatingIn(false);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimatingOut) return null;
  if (!planData) return null;

  const handlePlanSelect = (duration: string) => {
    setSelectedPlan(duration);
  };

  const getSelectedPlanPrice = (): number => {
    if (!selectedPlan) return 0;
    const option = planData.pricingOptions.find(
      (option) => option.duration === selectedPlan,
    );
    return option ? parseFloat(option.price) : 0;
  };

  const handleProceedClick = () => {
    if (selectedPlan) {
      onProceedToPayment(selectedPlan);
    }
  };

  const handleClose = () => {
    setIsAnimatingIn(false);
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsAnimatingOut(false);
      onClose();
    }, 300);
  };

  const handleBackdropClick = () => {
    handleClose();
  };

  const handleDragHandleClick = () => {
    handleClose();
  };

  const selectedPlanPrice = getSelectedPlanPrice();
  const finalTotal = selectedPlan ? calculateGST(selectedPlanPrice).total : 0;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isAnimatingIn && !isAnimatingOut ? "bg-opacity-50" : "bg-opacity-0"
        }`}
        onClick={handleBackdropClick}
      />

      <div
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 transform transition-transform duration-300 ease-out max-h-[90vh] overflow-y-auto ${
          isAnimatingIn && !isAnimatingOut
            ? "translate-y-0"
            : "translate-y-full"
        }`}
      >
        <div className="px-6 py-4">
          <div className="flex justify-center mb-3">
            <div
              className="w-12 h-1 bg-gray-300 rounded-full cursor-pointer hover:bg-gray-400 transition-colors duration-200"
              onClick={handleDragHandleClick}
            ></div>
          </div>

          <div className="mb-6">
            <h3 className="text-[16px] font-medium text-[#202020] mb-2">
              Select plan
            </h3>
          </div>

          <div className="space-y-3 mb-6">
            {planData.pricingOptions.map((option, index) => {
              const hasDiscount =
                option.originalPrice && option.originalPrice !== option.price;

              return (
                <button
                  key={index}
                  onClick={() => handlePlanSelect(option.duration)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-[8px] border transition-colors ${
                    selectedPlan === option.duration
                      ? "border-[#0DB5B5] bg-[#F1FBFB]"
                      : "border-[#6DD0CF] bg-white"
                  }`}
                >
                  <span className="text-[12px] font-medium text-[#202020]">
                    {option.duration}
                  </span>

                  <div className="flex items-center gap-2">
                    {hasDiscount && (
                      <span className="text-[12px] text-[#646464] line-through">
                        ₹{option.originalPrice}
                      </span>
                    )}
                    <div className="flex items-center">
                      <span className="text-[16px] font-semibold text-[#007E7F] mr-1">
                        ₹
                      </span>
                      <span className="text-lg font-semibold text-[#0DB5B5]">
                        {option.price}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center mb-6 text-sm text-[#202020]">
            <AlertCircle className="w-4 h-4 mr-2" />
            <span>No auto-renewal. Top-up anytime.</span>
          </div>

          {selectedPlan && (
            <div className="mb-6">
              <GSTCalculator baseAmount={selectedPlanPrice} className="mb-4" />
            </div>
          )}

          <button
            onClick={handleProceedClick}
            disabled={!selectedPlan}
            className={`w-full py-4 rounded-xl font-medium transition-colors ${
              selectedPlan
                ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {selectedPlan
              ? `Proceed with ₹${finalTotal.toFixed(2)}`
              : "Select a plan"}
          </button>
        </div>
      </div>
    </>
  );
};

// Features Table Component
interface FeaturesTableProps {
  featuresTable: {
    headers: string[];
    rows: {
      feature: string;
      values: string[];
    }[];
  };
}

const FeaturesTable: React.FC<FeaturesTableProps> = ({ featuresTable }) => {
  return (
    <div className="mb-6">
      <div className="rounded-xl border-[0.5px] border-[#6DD0CF] overflow-hidden">
        {/* Header (with background) */}
        <div
          className="grid bg-[#F3FBFB]"
          style={{ gridTemplateColumns: "50% repeat(3, 1fr)" }}
        >
          <div className="px-3 py-2 text-left">
            <span className="text-[12px] font-semibold text-[#1F1F1F]">
              Feature
            </span>
          </div>

          {featuresTable.headers.map((header, index) => (
            <div key={index} className="px-3 py-2 text-center">
              <span className="text-[12px] font-semibold text-[#1F1F1F]">
                {header}
              </span>
            </div>
          ))}
        </div>

        {/* Rows (no background) */}
        {featuresTable.rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="grid border-t-[0.5px] border-[##E0E0E0] bg-white"
            style={{ gridTemplateColumns: "50% repeat(3, 1fr)" }}
          >
            <div className="px-3 py-2 text-left">
              <span className="text-[12px] text-[#646464]">{row.feature}</span>
            </div>

            {row.values.map((value, colIndex) => (
              <div key={colIndex} className="px-3 py-2 text-center">
                <span className="text-[12px] text-[#646464]">{value}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Component
const RecommendedPlanDetails: React.FC<RecommendedPlanDetailsProps> = ({
  planType,
  userId,
  authToken,
  onBack,
  onSelectPlan,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPlanData();
  }, [planType]);

  const fetchPlanData = async () => {
    try {
      setError(null);
      const response = await fetch(API_URLS.Get_STANDARD_PLANS);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data: ApiResponse = await response.json();
      if (data.success !== "1")
        throw new Error(data.message || "Failed to fetch plans");

      const apiPlan = findPlanByType(data.plans, planType);
      if (!apiPlan) throw new Error(`Plan type "${planType}" not found`);

      const transformedPlan = transformApiDataToPlanData(apiPlan, planType);
      setPlanData(transformedPlan);
    } catch (err) {
      console.error("Error fetching plan data:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );
    } finally {
      setLoading(false);
    }
  };

  const findPlanByType = (
    plans: ApiPlanData[],
    type: string,
  ): ApiPlanData | null => {
    const planMappings = {
      lite: "Triva Lite",
      spark: "Triva Spark",
      ignite: "Triva Ignite",
      thrive: "Triva Thrive",
    };

    return (
      plans.find((plan) =>
        plan.subscription_plan_name
          .toLowerCase()
          .includes(
            planMappings[type as keyof typeof planMappings].toLowerCase(),
          ),
      ) || null
    );
  };

  const parsePerksToArray = (perks: string): string[] => {
    if (!perks) return [];
    let cleanedPerks = perks
      .replace(/<[^>]*>/g, "")
      .replace(/\\r\\n|\\r|\\n|\r\n|\r|\n/g, "|BREAK|")
      .trim();

    if (!cleanedPerks.includes("|BREAK|")) {
      return cleanedPerks ? [cleanedPerks] : [];
    }

    return cleanedPerks
      .split("|BREAK|")
      .map((perk) => perk.trim())
      .filter((perk) => perk && perk.length > 0)
      .map((perk) => perk.replace(/,$/, ""));
  };

  const parsePerfectFor = (perfectForString: string): string[] => {
    if (!perfectForString) return [];
    return perfectForString
      .replace(/"/g, "")
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const formatDuration = (duration: string): string => {
    const mapping = {
      "1 Month": "For 1 month @",
      "3 Months": "For 3 month @",
      "6 Months": "For 6 month @",
      "1 time": "For 1 time @",
    };
    return mapping[duration as keyof typeof mapping] || `For ${duration} @`;
  };

  // Parse features table from API data
  const parseFeaturesTable = (
    apiPlan: ApiPlanData,
  ):
    | {
        headers: string[];
        rows: {
          feature: string;
          values: string[];
        }[];
      }
    | undefined => {
    // Check if any duration has features data
    const featuresData = apiPlan.duration_and_price_and_perks.find(
      (option) =>
        option.std_subscription_features &&
        option.std_subscription_features.trim() !== "",
    );

    if (!featuresData || !featuresData.std_subscription_features) {
      return undefined;
    }

    const features = featuresData.std_subscription_features;

    // Parse the features string
    // Expected format: "Feature1:val1,val2,val3\n<br>Feature2:val1,val2,val3"
    const rows = features
      .replace(/<br>/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const [feature, valuesStr] = line.split(":");
        const values = valuesStr
          ? valuesStr.split(",").map((v) => v.trim())
          : [];
        return {
          feature: feature.trim(),
          values,
        };
      })
      .filter((row) => row.values.length > 0);

    if (rows.length === 0) {
      return undefined;
    }

    // Determine headers based on duration options (1m, 3m, 6m)
    const durations = apiPlan.duration_and_price_and_perks
      .map((option) => option.duration_period)
      .sort((a, b) => {
        const durationOrder = { "1 Month": 1, "3 Months": 2, "6 Months": 3 };
        return (
          (durationOrder[a as keyof typeof durationOrder] || 999) -
          (durationOrder[b as keyof typeof durationOrder] || 999)
        );
      });

    const headers = durations.map((duration) => {
      if (duration === "1 Month") return "1m";
      if (duration === "3 Months") return "3m";
      if (duration === "6 Months") return "6m";
      return duration;
    });

    return {
      headers,
      rows,
    };
  };

  const transformApiDataToPlanData = (
    apiPlan: ApiPlanData,
    type: string,
  ): PlanData => {
    const staticConfig =
      staticPlanConfigs[type as keyof typeof staticPlanConfigs];
    const firstOption = apiPlan.duration_and_price_and_perks[0];

    // ✅ CHANGED: Use discount price for calculations, original price for strikethrough
    const pricingOptions = apiPlan.duration_and_price_and_perks
      .sort((a, b) => {
        const durationOrder = { "1 Month": 1, "3 Months": 2, "6 Months": 3 };
        return (
          (durationOrder[a.duration_period as keyof typeof durationOrder] ||
            999) -
          (durationOrder[b.duration_period as keyof typeof durationOrder] ||
            999)
        );
      })
      .map((option) => ({
        duration: formatDuration(option.duration_period),
        price: option.std_subscription_discount_price, // ✅ Use discounted price for calculations
        originalPrice:
          option.std_subscription_discount_price !==
          option.std_subscription_price
            ? option.std_subscription_price
            : undefined, // ✅ Original price is the non-discounted one
        productId: option.product_id,
        standardDetailsId: option.standard_details_id,
      }));

    const planNameMatch = apiPlan.subscription_plan_name.match(
      /^([^(]+)(?:\s*\(([^)]+)\))?/,
    );
    const title = planNameMatch
      ? planNameMatch[1].trim()
      : apiPlan.subscription_plan_name;
    const description =
      planNameMatch && planNameMatch[2] ? planNameMatch[2].trim() : "";

    // ✅ CHANGED: Calculate lowest price from discount prices
    const prices = pricingOptions.map((option) => parseFloat(option.price));
    const lowestPrice = prices.length > 0 ? Math.min(...prices) : 0;

    let lowestOriginalPrice: string | undefined = undefined;
    if (prices.length > 0) {
      const lowestPriceOption = pricingOptions.find(
        (option) => parseFloat(option.price) === lowestPrice,
      );

      if (lowestPriceOption && lowestPriceOption.originalPrice) {
        lowestOriginalPrice = lowestPriceOption.originalPrice;
      }
    }

    const featuresTable = parseFeaturesTable(apiPlan);

    return {
      title,
      description,
      price: lowestPrice.toString(),
      originalPrice: lowestOriginalPrice,
      priceLabel: type === "spark" ? "" : "/month",
      ...staticConfig,
      summary: firstOption?.std_subscription_description || "",
      perfectFor: parsePerfectFor(
        firstOption?.std_subscription_perfect_for || "",
      ),
      whatYouGet: parsePerksToArray(apiPlan.std_subscription_perks),
      whatsNotIncluded: firstOption?.std_subscription_not_included
        ? [firstOption.std_subscription_not_included]
        : [],
      featuresTable,
      pricingOptions,
    };
  };

  const handleSelectPlanClick = () => {
    if (planData) {
      setShowPopup(true);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleProceedToPayment = (selectedDuration: string) => {
    if (planData) {
      setShowPopup(false);
      onSelectPlan(planData, selectedDuration);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <div className="flex items-center bg-white mb-4">
          <button onClick={onBack} className="flex items-center gap-1">
            <ChevronLeft className="w-6 h-6 text-[#173430]" />
            <span className="text-[14px] font-medium text-[#202020]">Back</span>
          </button>
        </div>
        <div className="space-y-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-48"></div>
          <div className="h-32 bg-gray-200 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !planData) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <div className="flex items-center bg-white mb-4">
          <button onClick={onBack} className="flex items-center gap-1">
            <ChevronLeft className="w-6 h-6 text-[#173430]" />
            <span className="text-[14px] font-medium text-[#202020]">Back</span>
          </button>
        </div>
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-[18px] font-semibold text-[#202020] mb-2">
            Unable to Load Plan
          </h2>
          <p className="text-[#646464] mb-4">
            {error || "Plan data not available"}
          </p>
          <button
            onClick={fetchPlanData}
            className="bg-[#0DB5B5] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#0A9999] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <div className="flex items-center bg-white mb-4">
          <button onClick={onBack} className="flex items-center gap-1">
            <ChevronLeft className="w-6 h-6 text-[#173430]" />
            <span className="text-[14px] font-medium text-[#202020]">Back</span>
          </button>
        </div>

        <div className="pb-24">
          {/* Plan Card */}
          <div
            className={`relative rounded-2xl overflow-hidden border ${planData.borderColor} mb-4`}
            style={{ boxShadow: "0px 0px 6px 0px #0837370F" }}
          >
            <div className="absolute inset-0 w-full h-full">
              <planData.BgImageComponent className="w-full h-full object-cover" />
            </div>

            <div className="relative z-10">
              <div className="flex items-start p-4 pb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  {planData.icon}
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <h3 className="text-[18px] font-semibold text-[#202020]">
                    {planData.title}
                  </h3>
                  <p className="text-[10px] text-[#646464]">
                    {planData.description}
                  </p>
                </div>
              </div>

              <div
                className="w-full h-[1px]"
                style={{
                  backgroundImage: `repeating-linear-gradient(to right, ${planData.borderHex} 0, ${planData.borderHex} 8px, transparent 8px, transparent 16px)`,
                }}
              ></div>

              <div
                className={`${planData.bgColor} px-4 py-2 flex items-center justify-between`}
              >
                <span className="text-[12px] font-inter font-normal text-[#202020]">
                  Starting from
                </span>
                <div className="flex items-center gap-2">
                  {planData.originalPrice &&
                    planData.originalPrice !== planData.price && (
                      <span className="text-[10px] font-inter text-[#646464] line-through">
                        ₹{planData.originalPrice}
                      </span>
                    )}

                  <div className="flex items-center">
                    <span className="text-[12px] font-inter mr-1 text-[#202020]">
                      ₹
                    </span>
                    <span className="text-[14px] font-bold font-inter mr-1 text-[#202020]">
                      {planData.price}
                    </span>
                    <span className="text-[10px] font-medium font-inter text-[#202020]">
                      {planData.priceLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {planData.summary && (
            <div className="mb-4">
              <p className="text-[12px] text-[#202020] font-medium font-inter leading-relaxed">
                {planData.summary}
              </p>
            </div>
          )}

          {planData.perfectFor.length > 0 && (
            <div className="mb-4">
              <h2 className="text-[14px] font-semibold text-[#202020] mb-3">
                Perfect for:
              </h2>
              <div className="flex flex-wrap gap-2">
                {planData.perfectFor.map((item, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-[#F1FBFB] text-[#003E3E] rounded-full text-[12px] font-normal border-[0.5px] border-[#6DD0CF]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Features Table */}
          {planData.featuresTable && (
            <FeaturesTable featuresTable={planData.featuresTable} />
          )}

          {planData.whatYouGet.length > 0 && (
            <div className="mb-4">
              <h2 className="text-[14px] font-semibold text-[#202020] mb-2">
                What you get:
              </h2>
              <div className="space-y-2">
                {planData.whatYouGet.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <Check className="w-3 h-3 text-[#003E3E] mr-2 mt-1.5 flex-shrink-0" />
                    <span className="text-[#202020] text-sm leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {planData.whatsNotIncluded.length > 0 && (
            <div className="mb-6">
              <h2 className="text-[14px] font-semibold text-[#202020] mb-2">
                What's not included
              </h2>
              <div className="space-y-3">
                {planData.whatsNotIncluded.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <AlertCircle className="w-3 h-3 text-[#FF3B30] mr-2 mt-1.5 flex-shrink-0" />
                    <span className="text-[#202020] text-sm leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className="fixed -bottom-0.5 left-0 right-0 p-4"
          style={{ backdropFilter: "blur(10px)" }}
        >
          <button
            onClick={handleSelectPlanClick}
            className="w-full py-4 rounded-xl font-medium text-[16px] transition-colors duration-200 bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
          >
            Select plan
          </button>
        </div>
      </div>

      {/* Plan Selection Popup */}
      <PlanSelectionPopup
        isOpen={showPopup}
        onClose={handleClosePopup}
        planData={planData}
        onProceedToPayment={handleProceedToPayment}
      />
    </>
  );
};

export default RecommendedPlanDetails;
